import moment from 'moment'

/** user_probation / Manage Users decision values — no longer actively on probation. */
export const PROBATION_COMPLETED_STATUSES = ['accepted', 'rejected', 'completed', 'inactive', 'closed']

/**
 * Countable open probation statuses (case-insensitive).
 * Also allow null / empty / missing — see isOpenProbationStatus.
 */
export const PROBATION_OPEN_STATUSES = ['extended']

/** Probation decision values that may appear on user.status when probation_status is absent. */
const PROBATION_DECISION_STATUSES = ['accepted', 'rejected', 'extended', 'completed']

/**
 * Resolve probation decision status from HR / user_probation records.
 * Does not treat employment Active/Inactive as probation status.
 */
export function resolveProbationStatus(record) {
  if (!record) return ''
  if (record.probation_status != null) {
    return String(record.probation_status).trim().toLowerCase()
  }
  if (record.probationStatus != null) {
    return String(record.probationStatus).trim().toLowerCase()
  }
  const status = record.status
  if (status != null && PROBATION_DECISION_STATUSES.includes(String(status).trim().toLowerCase())) {
    return String(status).trim().toLowerCase()
  }
  return ''
}

export function isProbationStatusCompleted(recordOrStatus) {
  const status = typeof recordOrStatus === 'string' || recordOrStatus == null
    ? String(recordOrStatus != null ? recordOrStatus : '').trim().toLowerCase()
    : resolveProbationStatus(recordOrStatus)
  return PROBATION_COMPLETED_STATUSES.includes(status)
}

/**
 * People on Probation allowlist: null / empty / missing, OR Extended.
 * Excludes Accepted, Rejected, Completed, Inactive, Closed, Active, Pending, etc.
 */
export function isOpenProbationStatus(recordOrStatus) {
  const status = typeof recordOrStatus === 'string' || recordOrStatus == null
    ? String(recordOrStatus != null ? recordOrStatus : '').trim().toLowerCase()
    : resolveProbationStatus(recordOrStatus)
  if (!status) return true
  return PROBATION_OPEN_STATUSES.includes(status)
}

/** Keep only Null/Extended probation rows; optional completed emp_id set for API fallbacks. */
export function filterActiveProbationRows(rows, completedEmpIds) {
  const completedIds = completedEmpIds instanceof Set
    ? completedEmpIds
    : new Set((completedEmpIds || []).map(id => String(id)))
  return (rows || []).filter((item) => {
    if (!item) return false
    if (!isOpenProbationStatus(item)) return false
    const empId = item.emp_id || item.employee_id
    if (empId != null && completedIds.has(String(empId))) return false
    return true
  })
}

/** Parse probation end dates from HR / API (Manage Users formats). */
export function parseProbationEndDate(value) {
  if (value == null || value === '') return null
  if (moment.isMoment(value)) return value.clone().startOf('day')
  const raw = String(value).trim()
  if (!raw) return null
  const formats = [
    moment.ISO_8601,
    'YYYY-MM-DD',
    'YYYY/MM/DD',
    'DD-MM-YYYY',
    'DD/MM/YYYY',
    'DD-MMM-YYYY',
    'DD MMM YYYY',
    'MMM DD, YYYY',
  ]
  const parsed = moment(raw, formats, true)
  if (parsed.isValid()) return parsed.startOf('day')
  const loose = moment(raw)
  return loose.isValid() ? loose.startOf('day') : null
}

/** Resolve raw probation end date string from a user / probation record. */
export function resolveProbationEndDateRaw(probation) {
  if (!probation) return null
  const doj = probation.doj || probation.start_date || probation.joining_date || probation.date_of_joining
  const probationDays = probation.probation_days != null
    ? probation.probation_days
    : probation.probationDays
  return probation.probation_end_date
    || probation.probationEndDate
    || probation.end_date
    || probation.endDate
    || (probationDays != null && doj
      ? moment(doj).add(Number(probationDays) || 0, 'days').format('YYYY-MM-DD')
      : null)
}

/**
 * Canonical probation end date + days remaining for dashboard UI.
 * Prefers explicit HR end_date over probation_pending_days (matches Manage Users).
 */
export function computeProbationTiming(probation, todayInput) {
  const today = todayInput
    ? (moment.isMoment(todayInput) ? todayInput.clone().startOf('day') : moment(todayInput).startOf('day'))
    : moment().startOf('day')

  const endDateRaw = resolveProbationEndDateRaw(probation)
  if (endDateRaw) {
    const endDate = parseProbationEndDate(endDateRaw)
    if (endDate) {
      return {
        probationEndDate: endDate.format('YYYY-MM-DD'),
        daysRemaining: endDate.diff(today, 'days'),
      }
    }
  }

  const pendingRaw = probation.probation_pending_days != null
    ? probation.probation_pending_days
    : probation.probationPendingDays
  const pendingDays = pendingRaw != null && pendingRaw !== ''
    ? Number(pendingRaw)
    : null

  if (pendingDays != null && !Number.isNaN(pendingDays)) {
    const endFromPending = pendingDays > 0
      ? today.clone().add(pendingDays, 'days')
      : today.clone()
    return {
      probationEndDate: endFromPending.format('YYYY-MM-DD'),
      daysRemaining: pendingDays,
    }
  }

  return {
    probationEndDate: null,
    daysRemaining: null,
  }
}

/** Short label for Needs attention probation rows. */
export function formatProbationDaysLabel(daysRemaining, endDate, formatDate, probationStatus) {
  const status = typeof probationStatus === 'string'
    ? probationStatus
    : resolveProbationStatus(probationStatus)
  if (status === 'accepted' || status === 'completed') return 'Accepted'
  if (status === 'rejected') return 'Rejected'

  if (daysRemaining != null) {
    const days = Number(daysRemaining)
    if (!Number.isNaN(days)) {
      if (days < 0) return 'Overdue'
      if (days === 0) return 'Ends today'
      return `${days}d left`
    }
  }
  if (endDate && formatDate) return formatDate(endDate)
  return 'On probation'
}

/** Reason line for Alerts & Notifications probation rows. */
export function formatProbationAlertReason(daysRemaining, endDate, formatDate, probationStatus) {
  const status = typeof probationStatus === 'string'
    ? probationStatus
    : resolveProbationStatus(probationStatus)
  if (status === 'accepted' || status === 'completed') return 'Probation accepted'
  if (status === 'rejected') return 'Probation rejected'

  let daysLabel = 'On probation'
  if (daysRemaining != null) {
    const days = Number(daysRemaining)
    if (!Number.isNaN(days)) {
      if (days < 0) daysLabel = 'Overdue'
      else if (days === 0) daysLabel = 'Ends today'
      else daysLabel = `${days} day${days === 1 ? '' : 's'} left`
    }
  }
  return endDate && formatDate ? `Ends ${formatDate(endDate)} · ${daysLabel}` : daysLabel
}
